// =============================================================================
// Painel White Label — Auditoria
// Lista o histórico de ações executadas no painel /wl. Layout padronizado no
// shell ListPageLayout (mesmo modelo de WlTenants/WlSupportSessions).
//
// RELEASE: 2026-05-30 v2.0.0 - Refator para ListPageLayout + KPIs (Total/Hoje/
//                              Inserts/Updates/Deletes) + filtros Operador/
//                              Tenant/Ação + CrmPeriodFilter + PaginationFooter.
// RELEASE: 2026-05-28 v1.0.0 - Versao inicial.
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ScrollText,
  Search,
  RefreshCw,
  CalendarPlus,
  PlusCircle,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from "@/components/layout/list-page";
import { PaginationFooter } from "@/components/ui/pagination-footer";
import { CrmPeriodFilter } from "@/components/crm/filters/CrmPeriodFilter";
import {
  calculatePeriodDates,
  type PeriodFilter,
  type PeriodPreset,
} from "@/hooks/useCrmOverviewFilters";
import { WlOperatorFilter } from "@/components/wl/filters/WlOperatorFilter";
import { WlTenantFilter } from "@/components/wl/filters/WlTenantFilter";
import { cn } from "@/lib/utils";

// ---------- Tipos ----------
interface AuditRow {
  id: string;
  created_at: string;
  wl_user_id: string | null;
  wl_user_name: string | null;
  wl_user_email: string | null;
  action: string;
  menu_key: string | null;
  target_tenant_id: string | null;
  target_tenant_name: string | null;
  target_entity: string | null;
  target_entity_id: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload_before: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload_after: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: any;
  total_count: number;
}

// Lista fixa de menus do painel WL (espelha o que existe no sidebar).
const MENU_OPTIONS = [
  "wl.dashboard",
  "wl.operators",
  "wl.roles",
  "wl.tenants",
  "wl.tenant_users",
  "wl.instances",
  "wl.financial",
  "wl.audit",
  "wl.settings",
];

// Verbos comuns; "busca livre" cobre o resto via Input.
const ACTION_OPTIONS = ["INSERT", "UPDATE", "DELETE", "LOGIN"];

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

const fmtDateTime = (v: string) =>
  new Date(v).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "medium",
  });

const truncId = (v: string | null) =>
  v ? v.substring(0, 8).toUpperCase() : "—";

/** Retorna a cor base da bolinha conforme verbo da ação. */
function actionDotColor(action: string): string {
  const a = (action || "").toUpperCase();
  if (a.startsWith("INSERT")) return "bg-green-500";
  if (a.startsWith("UPDATE")) return "bg-yellow-500";
  if (a.startsWith("DELETE")) return "bg-red-500";
  if (a.startsWith("LOGIN")) return "bg-blue-500";
  return "bg-zinc-400";
}

/** Renderiza bolinha colorida + texto (substitui Badge balão). */
function DotLabel({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-foreground whitespace-nowrap">
      <span className={cn("h-2 w-2 rounded-full shrink-0", color)} />
      {label}
    </span>
  );
}

export default function WlAudit() {
  const { toast } = useToast();

  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Busca textual (Enter / onBlur dispara — padrao WlTenants)
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Filtros
  const [menuKey, setMenuKey] = useState<string>("__all");
  const [operatorFilter, setOperatorFilter] = useState<string>("__all");
  const [tenantFilter, setTenantFilter] = useState<string>("__all");
  const [actionFilter, setActionFilter] = useState<string>("__all");

  // KPI ativo (toggle de filtro por verbo)
  const [kpiFilter, setKpiFilter] = useState<string | null>(null);

  // Paginação 1-based
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  // Período (default: TODOS — só envia datas quando o usuário ativar)
  const [period, setPeriod] = useState<PeriodFilter>(() => {
    const { startDate, endDate } = calculatePeriodDates("month");
    return { preset: "month", startDate, endDate };
  });
  const [periodActive, setPeriodActive] = useState(false);

  // KPI "Hoje" força recorte de hoje (overlay sobre o período do usuário)
  const todayActive = kpiFilter === "today";

  // Lookups removidos: filtros de Operador e Tenant agora são componentes
  // dedicados (WlOperatorFilter / WlTenantFilter) com lookup interno.

  // Diálogo de detalhes
  const [selected, setSelected] = useState<AuditRow | null>(null);

  // Verbo derivado do KPI ativo (Inserts/Updates/Deletes)
  const kpiActionVerb = useMemo<string | null>(() => {
    if (kpiFilter === "inserts") return "INSERT";
    if (kpiFilter === "updates") return "UPDATE";
    if (kpiFilter === "deletes") return "DELETE";
    return null;
  }, [kpiFilter]);

  // Ação efetiva enviada à RPC (KPI tem prioridade sobre o Select).
  const effectiveAction = useMemo<string | null>(() => {
    if (kpiActionVerb) return kpiActionVerb;
    return actionFilter === "__all" ? null : actionFilter;
  }, [actionFilter, kpiActionVerb]);

  // Range de período enviado à RPC.
  const periodIso = useMemo(() => {
    if (todayActive) {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      return { from: start.toISOString(), to: end.toISOString() };
    }
    if (periodActive) {
      return {
        from: period.startDate.toISOString(),
        to: period.endDate.toISOString(),
      };
    }
    return { from: null as string | null, to: null as string | null };
  }, [period, periodActive, todayActive]);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("wl_list_audit_log", {
        p_search: search.trim() || null,
        p_wl_user_id: operatorFilter === "__all" ? null : operatorFilter,
        p_tenant_id: tenantFilter === "__all" ? null : tenantFilter,
        p_menu_key: menuKey === "__all" ? null : menuKey,
        p_action: effectiveAction,
        p_date_from: periodIso.from,
        p_date_to: periodIso.to,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
      });
      if (error) throw error;
      const list = (data ?? []) as AuditRow[];
      setRows(list);
      setTotal(list[0]?.total_count ?? 0);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Erro inesperado ao carregar.";
      toast({
        title: "Erro ao carregar auditoria",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    search,
    menuKey,
    operatorFilter,
    tenantFilter,
    actionFilter,
    kpiFilter,
    periodIso.from,
    periodIso.to,
  ]);

  const toggleKpi = (key: string | null) => {
    setKpiFilter((prev) => (prev === key ? null : key));
    setPage(1);
  };

  // Handlers do CrmPeriodFilter
  const handlePresetChange = (preset: PeriodPreset) => {
    if (preset === "custom") return;
    const { startDate, endDate } = calculatePeriodDates(preset);
    setPeriod({ preset, startDate, endDate });
    setPeriodActive(true);
    setPage(1);
  };
  const handleCustomChange = (startDate: Date, endDate: Date) => {
    setPeriod({ preset: "custom", startDate, endDate });
    setPeriodActive(true);
    setPage(1);
  };

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Auditoria"
        description="Histórico de ações executadas por operadores no painel White Label."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        }
      />

      <ListPageKpis columns={5}>
        <KpiCard
          color="purple"
          icon={ScrollText}
          label="Total"
          value={loading ? "…" : total}
          onClick={() => toggleKpi(null)}
          active={kpiFilter === null}
        />
        <KpiCard
          color="cyan"
          icon={CalendarPlus}
          label="Hoje"
          value={loading && todayActive ? "…" : todayActive ? total : "—"}
          onClick={() => toggleKpi("today")}
          active={kpiFilter === "today"}
        />
        <KpiCard
          color="green"
          icon={PlusCircle}
          label="Inserts"
          value={loading && kpiFilter === "inserts" ? "…" : kpiFilter === "inserts" ? total : "—"}
          onClick={() => toggleKpi("inserts")}
          active={kpiFilter === "inserts"}
        />
        <KpiCard
          color="yellow"
          icon={Pencil}
          label="Updates"
          value={loading && kpiFilter === "updates" ? "…" : kpiFilter === "updates" ? total : "—"}
          onClick={() => toggleKpi("updates")}
          active={kpiFilter === "updates"}
        />
        <KpiCard
          color="red"
          icon={Trash2}
          label="Deletes"
          value={loading && kpiFilter === "deletes" ? "…" : kpiFilter === "deletes" ? total : "—"}
          onClick={() => toggleKpi("deletes")}
          active={kpiFilter === "deletes"}
        />
      </ListPageKpis>

      <ListPageFiltersBar isFetching={loading}>
        <div className="relative flex-1 min-w-[220px] sm:flex-none sm:w-[280px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            className={cn(LIST_FILTERS_INPUT_CLASS, "pl-7 sm:pl-8")}
            placeholder="Buscar por ação, entidade, operador, tenant…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSearch(searchInput);
                setPage(1);
              }
            }}
            onBlur={() => {
              if (searchInput !== search) {
                setSearch(searchInput);
                setPage(1);
              }
            }}
          />
        </div>

        <Select
          value={menuKey}
          onValueChange={(v) => {
            setMenuKey(v);
            setPage(1);
          }}
        >
          <SelectTrigger
            className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "sm:w-[160px]")}
          >
            <SelectValue placeholder="Menu" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todos os menus</SelectItem>
            {MENU_OPTIONS.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <WlOperatorFilter
          value={operatorFilter === "__all" ? null : operatorFilter}
          onChange={(v) => {
            setOperatorFilter(v ?? "__all");
            setPage(1);
          }}
          className="sm:w-[180px]"
        />

        <WlTenantFilter
          value={tenantFilter === "__all" ? null : tenantFilter}
          onChange={(v) => {
            setTenantFilter(v ?? "__all");
            setPage(1);
          }}
          className="sm:w-[200px]"
        />

        <Select
          value={actionFilter}
          onValueChange={(v) => {
            setActionFilter(v);
            setKpiFilter(null);
            setPage(1);
          }}
        >
          <SelectTrigger
            className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "sm:w-[140px]")}
          >
            <SelectValue placeholder="Ação" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todas ações</SelectItem>
            {ACTION_OPTIONS.map((a) => (
              <SelectItem key={a} value={a}>
                {a}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <CrmPeriodFilter
          value={period}
          onPresetChange={handlePresetChange}
          onCustomChange={handleCustomChange}
          className="h-6 text-[11px] sm:h-8 sm:text-sm"
        />
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        {loading ? (
          <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <ScrollText className="h-10 w-10 opacity-40" />
            Nenhum registro encontrado
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-medium">Data/Hora</TableHead>
                <TableHead className="text-xs font-medium">Operador</TableHead>
                <TableHead className="text-xs font-medium">Ação</TableHead>
                <TableHead className="text-xs font-medium">Menu</TableHead>
                <TableHead className="text-xs font-medium">Tenant alvo</TableHead>
                <TableHead className="text-xs font-medium">Entidade</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  <TableCell className="py-2 text-xs whitespace-nowrap text-muted-foreground">
                    {fmtDateTime(r.created_at)}
                  </TableCell>
                  <TableCell className="py-2 text-xs">
                    {/* Linha 1: nome do operador (fallback: email; fallback final: ID truncado) */}
                    <div className="text-foreground font-medium truncate">
                      {r.wl_user_name ??
                        r.wl_user_email ??
                        (r.wl_user_id ? truncId(r.wl_user_id) : "—")}
                    </div>
                    {/* Linha 2: email (apenas quando temos nome e email) */}
                    {r.wl_user_name && r.wl_user_email && (
                      <div className="text-[11px] text-muted-foreground truncate">
                        {r.wl_user_email}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="py-2">
                    <DotLabel
                      color={actionDotColor(r.action)}
                      label={r.action}
                    />
                  </TableCell>
                  <TableCell className="py-2">
                    {r.menu_key ? (
                      <DotLabel color="bg-zinc-400" label={r.menu_key} />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-xs">
                    {r.target_tenant_id ? (
                      <>
                        <div className="text-foreground">
                          {r.target_tenant_name ?? "—"}
                        </div>
                        <div className="text-[11px] font-mono text-muted-foreground">
                          {truncId(r.target_tenant_id)}
                        </div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-xs">
                    {r.target_entity ? (
                      <>
                        <div className="text-foreground">{r.target_entity}</div>
                        <div className="text-[11px] font-mono text-muted-foreground">
                          {truncId(r.target_entity_id)}
                        </div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListPageContent>

      <ListPageFooter>
        <PaginationFooter
          totalItems={total}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </ListPageFooter>

      {/* Diálogo de detalhes (preservado) */}
      <Dialog
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da auditoria</DialogTitle>
            <DialogDescription>
              {selected &&
                `${selected.action} • ${fmtDateTime(selected.created_at)}`}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Operador:</span>{" "}
                  {selected.wl_user_name ??
                    selected.wl_user_email ??
                    truncId(selected.wl_user_id)}
                </div>
                <div>
                  <span className="text-muted-foreground">Menu:</span>{" "}
                  {selected.menu_key ?? "—"}
                </div>
                <div>
                  <span className="text-muted-foreground">Tenant:</span>{" "}
                  {selected.target_tenant_name ??
                    truncId(selected.target_tenant_id)}
                </div>
                <div>
                  <span className="text-muted-foreground">Entidade:</span>{" "}
                  {selected.target_entity ?? "—"} (
                  {truncId(selected.target_entity_id)})
                </div>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">Antes</div>
                <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-60">
                  {selected.payload_before
                    ? JSON.stringify(selected.payload_before, null, 2)
                    : "—"}
                </pre>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">Depois</div>
                <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-60">
                  {selected.payload_after
                    ? JSON.stringify(selected.payload_after, null, 2)
                    : "—"}
                </pre>
              </div>
              {selected.metadata && (
                <div>
                  <div className="text-sm font-medium mb-1">Metadata</div>
                  <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-40">
                    {JSON.stringify(selected.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ListPageLayout>
  );
}
